import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { FlightProvider } from './Pages/Api File/FlightApi';
import { AuthProvider } from './IAP/contexts/AuthContext';
import { SubscriptionProvider } from './IAP/contexts/SubscriptionContext';
import SubscriptionChecker from './IAP/components/SubscriptionChecker';
import { ToastProvider } from './IAP/contexts/ToastContext';
import { FlightViewProvider } from './IAP/contexts/FlightViewContext';
import { TrialPopupPageReadyProvider } from './IAP/contexts/TrialPopupPageReadyContext';
import Ad5 from './Ad Folder/Ad5';
import NotificationPermission from './components/NotificationPermission';
import PerformanceMonitor from './components/PerformanceMonitor';
import FallbackStatusIndicator from './components/FallbackStatusIndicator';
import { Elements } from '@stripe/react-stripe-js';
import stripeConfig from './config/stripe';
import { trackFirstVisit, testPWATracking } from './utils/gtag';
import autoAdsManager from './utils/autoAdsManager';
import './utils/simpleAdLoader'; // Load simple ad loader
import { usePageTracking } from './usePageTracking';
// PERF: These were statically imported causing them to be in the initial bundle.
// Now lazy-loaded so they only load when their route is visited.
const ThankYouScreen = lazy(() => import('./IAP/components/ThankYouScreen'));
const IAP = lazy(() => import('./IAP/IAP'));
const SignIn = lazy(() => import('./Pages/Auth/SignIn'));
const SignUp = lazy(() => import('./Pages/Auth/SignUp'));

// Initialize Stripe promise lazily — only loads stripe.js when payment Elements actually mount.
// This prevents the ~130KB Stripe SDK from loading on blog, home, flight pages (reduces unused JS).
const stripePromise = new Promise((resolve) => {
  // Dynamic import so stripe.js is only fetched when Elements first renders
  import('@stripe/stripe-js').then(({ loadStripe }) => {
    resolve(loadStripe(stripeConfig.publishableKey));
  }).catch(() => resolve(null));
});
// import DebugUser from './debug-user'; // Debug component - can be removed
// Map is heavy (Leaflet/markers). Lazy-load to improve mobile Web Vitals.
const Map = lazy(() => import('./Pages/HomePage/ScreenMap/Map'));
// Other pages can stay lazily loaded
const Terms = lazy(() => import('./Pages/Terms/Terms'));
const About = lazy(() => import('./Pages/About/About'));
const AirlinePage = lazy(() => import('./Pages/HomePage/Airline/AirlinePage'));
const AirportCenter = lazy(() => import('./components/AirportCenter'));
const AirlineDetails = lazy(() => import('./Pages/HomePage/Airline/AirlineDetails'));
const AirportArrDep = lazy(() => import('./components/AirportArrDep'));
const FlightInfoPage = lazy(() => import('./Pages/HomePage/Flight Info Card/FlightInfoPage'));
const TrendingFlights = lazy(() => import('./Pages/TrendingFlight/TrendingFligths'));
const TopAirports = lazy(() => import('./Pages/TrendingFlight/TopAirports'));
const FlightSearchScreen = lazy(() => import('./Pages/HomePage/ScreenMap/FlightSearchScreen'));
const SubscriptionManagement = lazy(() => import('./IAP/SubscriptionManagement'));
const AdTestPage = lazy(() => import('./components/AdTestPage'));
const NotFound = lazy(() => import('./Pages/Not Found/NotFound'));
const BlogList = lazy(() => import('./components/Blog/BlogList'));
const BlogSlugRouter = lazy(() => import('./components/Blog/BlogSlugRouter'));
const Home = lazy(() => import('./Pages/HomePage/Home/Home'));
const AirportWithBlog = lazy(() => import('./Pages/HomePage/AirportWithBlog/AirportWithBlog'));
const AirlineWithBlog = lazy(() => import('./Pages/HomePage/AirportWithBlog/AirlineWithBlog/AirlineWithBlog'));
const FlightView = lazy(() => import('./components/3DFlight/3DFlightView'));
const FAQ = lazy(() => import('./Pages/Faq/Faq'));
const ForgotPassword = lazy(() => import('./Pages/Auth/ForgotPassword'));
const PrivacyPolicy = lazy(() => import('./Pages/Terms/PrivacyPolicy'));


// Preload critical components: Home (default route/LCP) first, then others
const preloadCriticalComponents = () => {
  import('./Pages/HomePage/Home/Home');
  import('./Pages/HomePage/Flight Info Card/FlightInfoPage');
  import('./components/AirportCenter');
};

// Preload heavy/secondary components when idle (keeps initial mobile load light)
const preloadIdleComponents = () => {
  import('./Pages/HomePage/ScreenMap/Map');
};

const PageTracker = () => {
  usePageTracking();
  return null;
};

// Auto Ads Route Tracker Component
const AutoAdsTracker = () => {
  useEffect(() => {
    // Enable Auto Ads immediately on mobile for revenue priority
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

    if (isMobile) {
      // Defer Auto Ads on mobile to reduce FCP/LCP/TBT impact
      const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      ric(() => {
        autoAdsManager.enableAutoAds();
      });
    } else {
      // Small delay on desktop
      const timer = setTimeout(() => {
        autoAdsManager.enableAutoAds();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return null;
};

const App = () => {
  // Defer non-critical components until after first paint to cut TBT (fewer long tasks up front)
  const [deferReady, setDeferReady] = useState(false);
  useEffect(() => {
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
    const id = ric(() => setDeferReady(true), { timeout: 3500 });
    return () => (typeof cancelIdleCallback !== 'undefined' ? cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  // Defer remote config init
  useEffect(() => {
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
    ric(() => {
      import('./remoteConfig').then(({ initRemoteConfig }) => {
        try { initRemoteConfig(); } catch (_) { }
      }).catch(() => { });
    });
  }, []);

  // Expose test function for debugging
  useEffect(() => {
    window.testPWATracking = testPWATracking;
    
    // Global error listener for chunk loading failures (resolves blank screen on redeploy)
    const handleChunkError = (e) => {
      if (e.message && (e.message.includes('Loading chunk') || e.message.includes('CSS_CHUNK_LOAD_FAILED'))) {
        console.log('🔄 Chunk loading failed, reloading for update...');
        window.location.reload(true);
      }
    };
    
    window.addEventListener('error', handleChunkError);
    return () => window.removeEventListener('error', handleChunkError);
  }, []);

  // Track first visit and preload critical chunks (Home = default route, improves LCP)
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    if (!hasVisited) {
      localStorage.setItem('hasVisited', 'true');
      trackFirstVisit();
    }

    // On mobile, keep initial JS as small as possible: do NOT preload extra routes.
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

    if (!isMobile) {
      // Desktop: preloading improves perceived navigation speed.
      preloadCriticalComponents();
      const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      ric(() => {
        preloadIdleComponents();
      });
    }
  }, []);

  return (
    <ToastProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <Elements stripe={stripePromise}>
            <FlightViewProvider>
              <Ad5 />
              <HelmetProvider>
                <FlightProvider>
                  <Router future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                  }}>
                    <TrialPopupPageReadyProvider>
                      <PageTracker />
                      <AutoAdsTracker />
                      {deferReady && <SubscriptionChecker />}
                      {deferReady && <NotificationPermission />}
                      {deferReady && <PerformanceMonitor />}
                      {/* Meta tags are handled by individual page components */}
                      <Suspense fallback={<div className="app-loading-screen" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#ffffff' }}><div className="app-loading-spinner" style={{ width: '40px', height: '40px', border: '4px solid rgba(0,0,0,0.1)', borderLeftColor: '#335A80', borderRadius: '50%', marginBottom: '16px', animation: 'app-spin 1s linear infinite' }}></div><style>{`@keyframes app-spin { to { transform: rotate(360deg); } }`}</style></div>}>
                        <Routes>
                          <Route path="/live-flights" element={<Map />} />
                          <Route path="/flight-info/:iata" element={<FlightInfoPage />} />
                          <Route path="/flight-info" element={<FlightInfoPage />} />
                          <Route path="/terms-and-conditions" element={<Terms />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/about-us" element={<About />} />
                          <Route path="/airline-tracker-24" element={<AirlinePage />} />
                          <Route path="/airports" element={<AirportCenter />} />
                          <Route path="/airline/:airlineIcao" element={<AirlineDetails />} />
                          <Route path="/airport/:iataCode" element={<AirportArrDep />} />
                          <Route path="/flay-radar" element={<TrendingFlights />} />
                          <Route path="/world-busiest-airports" element={<TopAirports />} />
                          <Route path="/flight-search" element={<FlightSearchScreen />} />
                          <Route path="/premium" element={<IAP />} />
                          <Route path="/subscription-management" element={<SubscriptionManagement />} />
                          <Route path="/thank-you" element={<ThankYouScreen />} />
                          <Route path="/ad-test" element={<AdTestPage />} />
                          <Route path="/blog" element={<BlogList />} />
                          <Route path="/blog/:slug" element={<BlogSlugRouter />} />
                          <Route path="/faqs" element={<FAQ />} />
                          <Route path="/" element={<Home />} />
                          <Route path="/airport-arrival-and-departure" element={<AirportWithBlog />} />
                          <Route path="/airlines" element={<AirlineWithBlog />} />
                          <Route path="/3DFlightView" element={<FlightView />} />
                          <Route path="/3DFlight/:flightIata" element={<FlightView />} />
                          <Route path="/sign-in" element={<SignIn />} />
                          <Route path="/sign-up" element={<SignUp />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </TrialPopupPageReadyProvider>
                  </Router>
                  <FallbackStatusIndicator />
                </FlightProvider>
              </HelmetProvider>
            </FlightViewProvider>
          </Elements>
        </SubscriptionProvider>
        {/* <DebugUser /> */}
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;